
import React from 'react';
import { Button } from './button';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import type { VariantProps } from 'class-variance-authority';
import { buttonVariants } from './button';
import { Calendar, ExternalLink } from 'lucide-react';

// Extract the allowed variants from the Button component
type ButtonVariant = NonNullable<VariantProps<typeof buttonVariants>["variant"]>;

export interface StyledButtonProps {
  children: React.ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary' | 'outline' | 'accent' | 'ghost';
  size?: 'default' | 'sm' | 'lg' | 'xl';
  href?: string;
  onClick?: (e: React.MouseEvent) => void;
  type?: 'button' | 'submit' | 'reset';
  disabled?: boolean;
  icon?: React.ReactNode;
  fullWidth?: boolean;
  ariaLabel?: string;
  external?: boolean;
  showCalendarIcon?: boolean; // Added for compatibility with ReserveButton
  bookingButton?: boolean; // Flag for booking buttons
}

const StyledButton = ({
  children,
  className,
  variant = 'primary',
  size = 'default',
  href,
  onClick,
  type = 'button',
  disabled = false,
  icon,
  fullWidth = false,
  ariaLabel,
  external = false,
  showCalendarIcon = false,
  bookingButton = false,
}: StyledButtonProps) => {
  // Map our custom variants to the shadcn Button variants
  const variantMap: Record<StyledButtonProps['variant'] & string, ButtonVariant> = {
    primary: 'default',
    secondary: 'secondary',
    outline: 'outline',
    accent: 'accent',
    ghost: 'ghost',
  };

  // Choose appropriate icon
  const finalIcon = showCalendarIcon ? <Calendar size={20} aria-hidden="true" /> : 
                    (external && !icon ? <ExternalLink size={18} className="ml-1" aria-hidden="true" /> : icon);

  const buttonContent = (
    <>
      {finalIcon && <span className="mr-2">{finalIcon}</span>}
      <span>{children}</span>
      {external && !finalIcon && <ExternalLink size={16} className="ml-1" aria-hidden="true" />}
    </>
  );

  // Generate appropriate aria label if not provided
  const accessibleLabel = ariaLabel || (typeof children === 'string' ? children : undefined);

  // For booking buttons, use specific styling
  const bookingStyles = bookingButton ? 
    "hover:scale-105 hover:-translate-y-1 hover:shadow-md" : "";

  // External link
  if (href && external) {
    return (
      <a
        href={href}
        className={cn(
          'inline-flex items-center justify-center',
          fullWidth && 'w-full',
          className
        )}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={accessibleLabel}
        onClick={onClick}
      >
        <Button
          variant={variantMap[variant]}
          size={size}
          disabled={disabled}
          className={cn(fullWidth && 'w-full', bookingStyles)}
        >
          {buttonContent}
        </Button>
      </a>
    );
  }

  // Internal link with router
  if (href) {
    return (
      <Link
        to={href}
        className={cn(
          'inline-flex items-center justify-center',
          fullWidth && 'w-full',
          className
        )}
        aria-label={accessibleLabel}
        onClick={onClick}
      >
        <Button
          variant={variantMap[variant]}
          size={size}
          disabled={disabled}
          className={cn(fullWidth && 'w-full', bookingStyles)}
        >
          {buttonContent}
        </Button>
      </Link>
    );
  }

  // Regular button
  return (
    <Button
      type={type}
      variant={variantMap[variant]}
      size={size}
      disabled={disabled}
      onClick={onClick}
      className={cn(fullWidth && 'w-full', bookingStyles, className)}
      aria-label={accessibleLabel}
    >
      {buttonContent}
    </Button>
  );
};

export default StyledButton;
